import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppButton, Box, Preloader } from '@common-fe/common-fe';

import { DIVIDING_MODE_MAIN_GROUP_KEY, LockboxStatus, LockboxValidationError } from '@/modules/employer/components/Lockbox/lockbox.types';
import useHighlightLockboxStatusStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useHighlightLockboxStatus.store';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { useLockboxDividingModeStore } from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDividingMode.store';
import useLockboxSplittingQuery, { MultipleRecordPayload } from '@/modules/employer/components/Lockbox/queries/useLockboxSplitting.query';

const DividingModeFooterActions = () => {
  const [hasFieldsValidation, setHasFieldsValidation] = useState(false);
  const { originalLockbox, validationErrors, setIsDividingMode, setValidationErrors } = useLockboxDetailsStore();
  const { groups, numberOfPages, reset } = useLockboxDividingModeStore();
  const { splitLockbox, isLoading } = useLockboxSplittingQuery({ lockboxId: originalLockbox?.id });
  const { setHighlightStatus } = useHighlightLockboxStatusStore();

  const handleExitFromDivingMode = useCallback(() => {
    setIsDividingMode(false);
    reset();
  }, [setIsDividingMode, reset]);

  const records: MultipleRecordPayload[] = useMemo(() => groups?.map((group) => ({
    main: group?.key === DIVIDING_MODE_MAIN_GROUP_KEY,
    pages: group?.items?.map((item) => item?.key)?.filter(Boolean)?.map((item) => Number(item)) || [],
  })), [groups]);

  const validate = useCallback(() => {
    const selectedPages = records?.map((record) => record?.pages)?.flat().length;
    if (selectedPages < numberOfPages) {
      return LockboxValidationError.ALL_PAGES_MUST_BE_SELECTED;
    }

    if (records?.length < 2) {
      return LockboxValidationError.MINIMUM_TWO_RECORDS_REQUIRED;
    }

    const emptyRecords = records?.find((record) => record?.pages?.length === 0);
    if (emptyRecords) {
      return LockboxValidationError.RECORDS_CANNOT_BE_EMPTY;
    }
  }, [records, numberOfPages]);

  const handleConfirmDividing = useCallback(async () => {
    const error = validate();

    if (error) {
      setValidationErrors([error]);
      setHasFieldsValidation(true);
      return;
    }

    await splitLockbox({ multiple_records: records });
    setHighlightStatus(LockboxStatus.DIVIDED);
    handleExitFromDivingMode();
  }, [records, splitLockbox, handleExitFromDivingMode, validate, setValidationErrors, setHighlightStatus]);

  useEffect(() => {
    if (hasFieldsValidation) {
      const error = validate();
      setValidationErrors(error ? [error] : []);
    }
  }, [hasFieldsValidation, validate, setValidationErrors]);

  const isValidationSuccessfully = useMemo(() => {
    return validationErrors?.length === 0;
  }, [validationErrors]);

  return (
    <>
      <Box margin={{ right: 'spacing12' }}>
        <AppButton
          testId="cancel-divining-mode"
          buttonType="secondary"
          color="red"
          disabled={isLoading}
          onClick={handleExitFromDivingMode}
        >
          Cancel
        </AppButton>
      </Box>

      <Box>
        <AppButton
          testId="confirm-dividing"
          onClick={handleConfirmDividing}
          disabled={isLoading || !isValidationSuccessfully}
        >
          {isLoading ? <Preloader color="white" /> : 'Confirm Dividing'}
        </AppButton>
      </Box>
    </>
  );
};

export default DividingModeFooterActions;
